import { useEffect, useState, useSyncExternalStore } from 'react';
import type { StatustextEntry, VehicleState } from '@hgc/core';
import type { LinkStats } from '@hgc/link';

import type { LinkSnapshot } from './LinkManager';
import type { VehicleSummary } from './VehicleRoster';
import { useAppStore } from './AppStoreContext';
import { recentNotices } from './notices';
import { getRoute, subscribeRoute, type SectionId } from '../app/route';

/**
 * React 与运行时的边界。
 *
 * 这一层只做一件事：把**已经存在的**可观察值接进渲染周期。所有 hook 都用
 * `useSyncExternalStore` 订阅，**不把值复制进 `useState`** ——
 * 复制一份再手动同步必然出现"两份真相"，而它的表现是某个面板的数字比别处慢一拍，
 * 只有在同时看两处时才发现。
 *
 * 由此得到一条规则：这里的每个 hook 都必须返回**引用稳定**的值
 * （`useSyncExternalStore` 用 `Object.is` 判断"变没变"，每次返回新对象会导致无限重渲染）。
 * 稳定引用由运行时保证（`Signal` 只在实际变化时替换值），这里不做任何 `useMemo` 包装。
 */

export function useLinkStatus(): LinkSnapshot {
  const { link } = useAppStore();
  return useSyncExternalStore(link.subscribeStatus, link.status, link.status);
}

export function useLinkStats(): LinkStats | null {
  const { link } = useAppStore();
  return useSyncExternalStore(link.subscribeStats, link.stats, link.stats);
}

export function useVehicleList(): readonly VehicleSummary[] {
  const { link } = useAppStore();
  return useSyncExternalStore(link.subscribeVehicles, link.vehicles, link.vehicles);
}

export function usePrimaryVehicle(): VehicleSummary | null {
  const { link } = useAppStore();
  return useSyncExternalStore(link.subscribeVehicles, link.primaryVehicle, link.primaryVehicle);
}

/**
 * 当前操作对象（焦点机体）的完整遥测快照。
 *
 * 只订阅**焦点那一辆**：一条链路上可能挂着伴飞计算机、云台、相机各一辆车，
 * 让每个面板自己从状态列表里挑一遍，等于把"该看谁"这个判断复制到每个面板里。
 */
export function useVehicleState(): VehicleState | null {
  const { link } = useAppStore();
  return useSyncExternalStore(link.subscribePrimaryState, link.primaryState, link.primaryState);
}

const EMPTY_NOTICES: readonly StatustextEntry[] = Object.freeze([]);

/**
 * 机体播报的状态文本（告警/通知）。
 *
 * 这是 `alerts` 管理器接入之前的**诚实替代**：它展示的是机体真的发过来的东西，
 * 而不是本地规则推断出来的告警。等 `@hgc/core` 的 alerts 接进来，这个 hook 的实现
 * 会换成告警总线，调用方（列表）一行都不用改。
 */
export function useNotices(limit?: number): readonly StatustextEntry[] {
  const state = useVehicleState();
  if (state === null) return EMPTY_NOTICES;
  return recentNotices(state.statustext, limit);
}

/**
 * 每秒走一次的"现在"。
 *
 * 存在的理由：新鲜度判断（`isTelemetryStale`）依赖当前时刻，而**没有新帧到达时
 * 组件不会重渲染** —— 也就是说，链路静默越久，界面越会停在"最后一次收到数据"的样子，
 * 而这恰好是最需要显示"这数据已经过期了"的时刻。
 */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return now;
}

/**
 * 当前分区。状态在 `app/route.ts` 的模块级 store 里（由 URL hash 决定），
 * 所以它跨组件卸载存活 —— 这正好与"连接必须活在 React 之外"是同一条原则。
 */
export function useSectionRoute(): SectionId {
  return useSyncExternalStore(subscribeRoute, getRoute, getRoute);
}
